/**
 * ConfirmCategoriesButton — primary "Confirm categories" action.
 *
 * Block M1a-FE (TZ 2026-07-29). После success → button → «Completed» + token display.
 * Idempotent (BE возвращает тот же token).
 *
 * Note for M1b: this button stays. After confirm-token the property-extraction
 * form replaces the `m1bHintBody` paragraph below — the i18n key is wired so
 * M1b can swap the hint copy for the actual form without breaking layout.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Sparkles } from 'lucide-react';

import { useConfirmCategories } from '@/hooks/useMatching';
import { useMatchingStore } from '@/store/matchingStore';
import { cn } from '@/lib/utils';

export function ConfirmCategoriesButton() {
  const { t } = useTranslation();
  const pipelineId = useMatchingStore((s) => s.pipelineId);
  const confirmToken = useMatchingStore((s) => s.confirmToken);
  const setConfirmToken = useMatchingStore((s) => s.setConfirmToken);
  const confirmCategories = useConfirmCategories();
  const [tokenCopied, setTokenCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!pipelineId || pipelineId.length === 0) return null;

  const busy = confirmCategories.isPending;

  function handleConfirm() {
    if (!pipelineId) return;
    setError(null);
    confirmCategories.mutate(pipelineId, {
      onSuccess: (resp) => {
        setConfirmToken(resp.confirm_token);
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : String(err));
      },
    });
  }

  async function handleCopy() {
    if (!confirmToken) return;
    try {
      await navigator.clipboard.writeText(confirmToken);
      setTokenCopied(true);
      window.setTimeout(() => setTokenCopied(false), 1500);
    } catch {
      // ignore
    }
  }

  return (
    <section className="card space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-400">
            {t('process.matching.confirm.tokenTitle', {
              defaultValue: 'Confirm categories',
            })}
          </h2>
          <p className="text-xs text-surface-500">
            {t('process.matching.confirm.tooltip', {
              defaultValue:
                'Finalises the category stage and unlocks the property stage',
            })}
          </p>
        </div>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={busy || Boolean(confirmToken)}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            confirmToken
              ? 'bg-success-tint/30 text-success cursor-default'
              : 'bg-success-strong hover:bg-success-hover text-white disabled:opacity-50',
          )}
        >
          {confirmToken ? (
            <Check className="w-4 h-4" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          {confirmToken
            ? t('process.matching.confirm.completedAtTitle', {
                defaultValue: 'Completed',
              })
            : t('process.matching.confirm.button', {
                defaultValue: 'Confirm categories',
              })}
        </button>
      </div>

      {/* M1b hint — wired so M1b can swap copy without touching layout */}
      {!confirmToken && (
        <p className="text-[11px] text-surface-500 italic">
          {t('process.matching.confirm.m1bHintBody', {
            defaultValue:
              'After confirming categories, M1b will add the property-extraction form here.',
          })}
        </p>
      )}

      {confirmToken && (
        <div className="rounded-lg bg-surface-900 border border-surface-700 p-3 space-y-1">
          <div className="text-xs uppercase tracking-wider text-surface-500">
            {t('process.matching.confirm.tokenTitle', {
              defaultValue: 'Confirm token',
            })}
          </div>
          <div className="flex items-center gap-2 font-mono text-xs text-success">
            <span className="break-all flex-1">{confirmToken}</span>
            <button
              type="button"
              onClick={handleCopy}
              className="p-1 rounded hover:bg-surface-700 text-surface-300"
              title={t('process.matching.confirm.tokenCopied', {
                defaultValue: 'Copied',
              })}
              aria-label="copy"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            {tokenCopied && (
              <span className="text-xs text-success">
                {t('process.matching.confirm.tokenCopied', {
                  defaultValue: 'Copied',
                })}
              </span>
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="text-xs text-danger">{error}</p>
      )}
    </section>
  );
}
